/**
 * MediaRecorder ラッパー。getUserMedia → MediaRecorder の取り扱いを useState で隠蔽する。
 *
 * テスト容易性のため、MediaRecorder と getUserMedia をオプションで差し替え可能にしている。
 *
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business voice-enrollment Web の録音 hook。getUserMedia → MediaRecorder の状態遷移を React state に集約
 * @graph-connects none
 */

import { useCallback, useEffect, useRef, useState } from 'react';

type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopped' | 'error';

interface RecorderState {
  status: RecorderStatus;
  /** 録音停止後の Blob。録音中は null */
  blob: Blob | null;
  error: string | null;
}

interface RecorderControls {
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
}

export interface UseRecorderOptions {
  /** テスト用 — getUserMedia 差し替え */
  getUserMediaImpl?: (constraints: MediaStreamConstraints) => Promise<MediaStream>;
  /** テスト用 — MediaRecorder 差し替え */
  mediaRecorderCtor?: typeof MediaRecorder;
  /** 録音 mime type。未指定時はブラウザデフォルト */
  mimeType?: string;
}

/**
 * @graph-stack voice-enrollment
 * @graph-domain Engineering
 * @graph-business 録音 hook 本体。状態 (idle/requesting/recording/stopped/error) と blob を返し、start/stop/reset を提供する
 * @graph-connects none
 */
export function useRecorder(options: UseRecorderOptions = {}): RecorderState & RecorderControls {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const cleanup = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        track.stop();
      }
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const start = useCallback(async () => {
    setStatus('requesting');
    setError(null);
    setBlob(null);
    chunksRef.current = [];
    try {
      const getUserMedia =
        options.getUserMediaImpl ??
        navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
      const stream = await getUserMedia({ audio: true });
      streamRef.current = stream;

      const Ctor = options.mediaRecorderCtor ?? MediaRecorder;
      const recorder = options.mimeType
        ? new Ctor(stream, { mimeType: options.mimeType })
        : new Ctor(stream);
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const finalBlob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        setBlob(finalBlob);
        setStatus('stopped');
      };
      recorder.start();
      recorderRef.current = recorder;
      setStatus('recording');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
      setStatus('error');
      cleanup();
    }
  }, [cleanup, options.getUserMediaImpl, options.mediaRecorderCtor, options.mimeType]);

  const stop = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      return;
    }
    await new Promise<void>((resolve) => {
      // start() で recorder.onstop を必ずセットしているため、ここでは
      // 元 handler の存在を前提に optional chain で呼ぶだけで十分。
      const original = recorder.onstop;
      recorder.onstop = (event) => {
        original?.call(recorder, event);
        resolve();
      };
      recorder.stop();
    });
    cleanup();
  }, [cleanup]);

  const reset = useCallback(() => {
    cleanup();
    setStatus('idle');
    setBlob(null);
    setError(null);
  }, [cleanup]);

  return { status, blob, error, start, stop, reset };
}
